<template>
  <div
    :id="pageId"
    :class="pageId"
    class="page-content"
  >
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          {{ formTitle }}
        </h3>
      </div>
      <hr class="card-title-line">
      <div class="card-content">
        <customized-form
          v-loading="status.isLoading"
          :element-loading-text="status.loadingMessage"
          element-loading-custom-class="custom-spinner-class"
          @submit="submitForm"
        >
          <div
            v-if="isBusinessAccount"
            class="row"
          >
            <label class="col-sm-3 control-label"> Custom Rider Name* </label>
            <div class="col-sm-7">
              <sanitized-input
                id="customRiderName"
                v-model="orderRideRequest.customRiderName"
                :disabled="!isEditableForm"
                placeholder="Please input rider name"
                required
              />
              <span class="help-block text-red"> This is a business account, please ask the caller for the rider's name </span>
            </div>
          </div>

          <div class="row">
            <label class="col-sm-3 control-label pt-10"> Phone Number* </label>
            <phone-number-input
              id="customer-phone"
              v-model="orderRideRequest.phoneNumber"
              :disabled="true"
              :page-id="pageId"
              :show-live-call="false"
              class="col-sm-7 col-10"
            >
              <template #help-block>
                Leave empty if you're getting a quote
              </template>
            </phone-number-input>

            <div
              v-if="isTransportation"
              class="col-sm-1 col-2 no-padding-width"
            >
              <button
                id="getBackupAccount"
                class="bug-button"
                type="button"
                @click="getOpenBackupAccount()"
              >
                🐛
              </button>
            </div>
          </div>

          <fieldset>
            <div class="row">
              <label class="col-sm-3 control-label"> Preferred Contact Number{{ isPreferredContactNumberRequired ? '*' : '' }} </label>
              <div class="col-sm-8 col-10">
                <dropdown-list
                  v-model="orderRideRequest.riderPhone"
                  :disabled="!isEditableForm"
                  :options="contactPhoneNumbers"
                />
                <phone-number-input
                  v-if="isNewPreferredContactNumberVisible"
                  id="contact-phone"
                  v-model="newPreferredContactNumber"
                  :disabled="!isEditableForm"
                  :page-id="pageId"
                  :required="isPreferredContactNumberRequired"
                  class="mt-3"
                >
                  <template #help-block>
                    <span
                      v-if="status.preferredContactWarning"
                      class="error-message"
                    >
                      {{ status.preferredContactWarning }}
                    </span>
                    <br v-if="status.preferredContactWarning">
                    <span
                      v-if="!status.preferredContactWarning"
                      :class="{ 'text-red': isBusinessAccount }"
                      class="help-block"
                    >
                      {{
                        isBusinessAccount
                          ? "This is a business account, please ask the caller for the rider's phone number"
                          : 'Put the number the client would like to be contacted'
                      }}
                    </span>
                  </template>
                </phone-number-input>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <div
              v-if="availablePartnerRidePurposes.length > 0"
              class="row align-items-center"
            >
              <label class="col-sm-3 control-label"> Ride Purpose* </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="orderRideRequest.partnerRidePurpose"
                  :disabled="!isEditableForm"
                  :options="availablePartnerRidePurposes"
                  required
                />
              </div>
            </div>

            <div class="row align-items-center">
              <label class="col-sm-3 control-label"> Service Type </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="orderRideRequest.serviceType"
                  :disabled="!isEditableForm"
                  :options="availableServiceTypeList"
                />
              </div>
            </div>

            <div v-if="isTransportation" class="row align-items-center">
              <label class="col-sm-3 control-label"> Type of Car* </label>
              <div class="col-sm-8">
                <dropdown-list
                  v-model="orderRideRequest.typeOfCar"
                  :disabled="!isEditableForm"
                  :options="availableRideTypes"
                  required
                />
              </div>
            </div>
          </fieldset>

          <ordering-address
            ref="startAddressComponent"
            v-model="startAddress"
            :anchor-bound="endAddressAnchorBound"
            :index="0"
            :is-disabled="!isEditableForm"
            :is-location-name-dialog-show="true"
            :is-location-name-show="!!orderRideRequest.phoneNumber"
            :is-phone-number-valid="isPhoneNumberValid"
            :ride-type="orderRideRequest.typeOfCar"
            :show100-miles-modal="true"
            :venues="pickupVenues"
            class="mobile-mt-5"
            is-geocode-show
            is-show-save-location
            map-pin-type="pickup"
            page-id="start-address"
            @swap="swapAddress"
            @venueNote="setVenueNote"
            @set-venue-places="setVenuePlaces"
            @change-marker-position="setMarkerPosition"
          >
            <template #title>
              <div class="mb-2">
                {{isDelivery ? 'Pickup Address' : 'Start Address'}}*
                <p-button
                  v-if="showEstimateStartCopyButton"
                  type="icon"
                  :icon="{ iconName: 'fa-flag-o', description: 'Use start address data from registration page' }"
                  @click="copyToStartAddress"
                />
              </div>
            </template>
          </ordering-address>

          <div>
            <div
              v-for="(stop, index) in stops"
              :key="stop._key"
              class="stop-address-wrapper mt-10"
            >
              <ordering-address
                v-model="stops[index]"
                :anchor-bound="startAddressAnchorBound"
                :index="index + 2"
                :is-address-required="false"
                :is-disabled="!isEditableForm"
                :is-location-name-show="!!orderRideRequest.phoneNumber"
                :is-phone-number-valid="isPhoneNumberValid"
                :ride-type="orderRideRequest.typeOfCar"
                :page-id="`stop-address-${index}`"
                is-geocode-show
              >
                <template #title>
                  <div class="stop-title-row mb-2">
                    <span>Stop {{ index + 1 }}</span>
                    <p-button
                      type="yellow"
                      round
                      :disabled="!isEditableForm"
                      class="ml-20"
                      @click="removeStop(index)"
                    >
                      Remove
                    </p-button>
                  </div>
                </template>
              </ordering-address>
            </div>

            <div class="add-stop-row mt-10 mb-10">
              <p-button
                v-if="canAddStop"
                type="purple"
                round
                :disabled="!isEditableForm"
                @click="addStop"
              >
                Add Stop
                <i class="fa fa-plus ml-10" />
              </p-button>

              <el-alert
                v-if="hasStops"
                :title="MULTI_STOP_WAIT_FEE_WARNING"
                type="warning"
                show-icon
                :closable="false"
                class="mt-10"
              />
            </div>
          </div>

          <ordering-address
            ref="endAddressComponent"
            v-model="endAddress"
            :anchor-bound="startAddressAnchorBound"
            :index="1"
            :is-address-required="false"
            :is-disabled="!isEditableForm"
            :is-location-name-show="!!orderRideRequest.phoneNumber"
            :is-phone-number-valid="isPhoneNumberValid"
            :ride-type="orderRideRequest.typeOfCar"
            :additionalPins="[markers[0]]"
            :venues="dropoffVenues"
            class="mt-10 mobile-mt-5 mb-30"
            is-geocode-show
            is-show-save-location
            map-pin-type="dropoff"
            page-id="end-address"
            @swap="swapAddress"
            @venueNote="setVenueNote"
            @set-venue-places="setVenuePlaces"
            @change-marker-position="setMarkerPosition"
          >
            <template #title>
              <div class="mb-2">
                {{isDelivery ? 'Delivery Address' : 'End Address'}}*
                <p-button
                  v-if="showEstimateEndCopyButton"
                  type="icon"
                  :icon="{ iconName: 'fa-flag-checkered', description: 'Use end address data from registration page' }"
                  @click="copyToEndAddress"
                />
              </div>
            </template>
          </ordering-address>

          <template v-if="isTransportation">
            <div class="row">
              <label class="col-sm-4 control-label pt-1"> What Are You Wearing Today? </label>
              <div class="col-sm-7">
                <sanitized-input
                  v-model="riderClothes"
                  placeholder="Describe clothing"
                />
              </div>
            </div>

            <div class="row">
              <label class="col-sm-4 control-label pt-1"> How Should the Driver Announce Their Arrival?* </label>
              <div class="col-sm-7">
                <dropdown-list
                  v-model="driverArrival"
                  :options="driverArrivals"
                  required
                />
              </div>
            </div>

            <div class="row">
              <PButton
                style="margin: auto;"
                type="purple"
                round
                @click="showAccessOptionsPopupDialog"
              >
                Edit Accessibility Options
              </PButton>
            </div>

            <div class="row">
              <div class="col-sm-8 offset-sm-3">
                <div>
                  <el-checkbox
                    id="noteGroceries"
                    v-model="orderRideRequest.defaultNoteMsg.groceries"
                  >
                    The rider has groceries.
                  </el-checkbox>
                </div>
                <div>
                  <el-checkbox
                    id="noteLuggage"
                    v-model="orderRideRequest.defaultNoteMsg.luggage"
                    class="pb-0"
                  >
                    The rider has luggage.
                  </el-checkbox>
                </div>
              </div>
            </div>

            <DriverAcceptedTextMessageNote
              v-model="orderRideRequest.noteToDriver"
              :driverNotePickUp="driverNotePickUp"
              :driverNoteDropOff="driverNoteDropOff"
              :serviceType="orderRideRequest.serviceType"
              :customRiderName="orderRideRequest.customRiderName"
              :isEmergency="orderRideRequest.rideType.isEmergency"
              :startAddress="startAddress"
              :infoForDriver="customerInfo.infoForDriver"
              :callerId="customerInfo.id"
              :lowTipAmount="estimationRide?.lowAutoTipAmount"
              :highTipAmount="estimationRide?.highAutoTipAmount"
              :isMultiStop="hasStops"
              :rideType="estimationRide?.rideType"
            />
          </template>

          <template v-if="!isNashDelivery">
            <div class="row">
              <div class="col-sm-8 offset-sm-3">
                <el-checkbox
                  id="isNeededToMonitorRide"
                  v-model="isNeededToMonitorRide"
                  :disabled="!isEditableForm"
                >
                  Have RSS monitor this ride
                </el-checkbox>
              </div>
            </div>

            <div
              v-if="isNeededToMonitorRide"
              class="row"
            >
              <label class="col-sm-3 control-label pt-5 mobile-pt-10">Reason for monitoring this ride..*</label>
              <div class="col-sm-8">
                <sanitized-textarea
                  v-model="orderRideRequest.notesForLP"
                  :disabled="!isEditableForm"
                  required
                  autosize
                />
              </div>
            </div>
          </template>

          <div
            v-if="isTransportation"
            class="row"
          >
            <label class="col-sm-3 control-label"> Ride Type </label>
            <div class="col-sm-8">
              <div>
                <el-checkbox
                  id="isEmergency"
                  v-model="orderRideRequest.rideType.isEmergency"
                  :class="orderRideRequest.rideType.isEmergency ? 'mobile-pb-0' : ''"
                  :disabled="!isEditableForm"
                >
                  Emergency Ride
                </el-checkbox>
                <div
                  v-if="orderRideRequest.rideType.isEmergency"
                  class="emergecy-info pb-10 mobile-pb-10"
                >
                  <label class="control-label"> Emergency Reason* </label>
                  <sanitized-input
                    id="emergencyReason"
                    v-model="orderRideRequest.rideType.emergencyReason"
                    :disabled="!isEditableForm"
                    placeholder="Please indicate the reason for the emergency ride"
                    required
                  />

                  <label class="control-label mt-10 mobile-mt-10"> Who Approved Emergency Ride?* </label>
                  <dropdown-list
                    v-model="orderRideRequest.rideType.approvedNameForEmergencyRide"
                    :disabled="!isEditableForm"
                    :options="teamLeadsAndSupsList"
                    filterable
                    required
                    label-key="name"
                    value-key="name"
                  />
                </div>
              </div>
              <div>
                <el-checkbox
                  id="isExpandDriverSearch"
                  v-model="isExpandDriverSearch"
                  :disabled="!isEditableForm"
                >
                  Expand Driver Search
                </el-checkbox>
              </div>

              <div v-if="isAllowedToExpandVehicleSearch">
                <el-checkbox
                  id="isExpandVehicleSearch"
                  v-model="isExpandVehicleSearch"
                  :disabled="!isEditableForm"
                >
                  Expand Vehicle Search
                </el-checkbox>
              </div>
            </div>
          </div>

          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Auto Retry Getting a </label>
            <div class="col-sm-8 d-flex align-items-center">
              <dropdown-list
                v-model="orderRideRequest.minutesToAutoRetryRequestType"
                :disabled="!isEditableForm"
                :options="minutesToAutoRetryRequestTypes"
              />

              &nbsp;for&nbsp;

              <number-input
                id="minutesToAutoRetryRequest"
                v-model="orderRideRequest.minutesToAutoRetryRequest"
                :disabled="!isEditableForm || orderRideRequest.minutesToAutoRetryRequestType === ''"
                :is-decimal="false"
                class="form-control"
                max="300"
                min="5"
                type="number"
              />
              &nbsp;&nbsp;minutes
            </div>
          </div>

          <div
            v-if="isShowDoNotApplyPartnerSubsidyField"
            class="row"
          >
            <div class="col-sm-9 offset-sm-3">
              <div>
                <el-checkbox
                  id="isDoNotApplySubsidy"
                  v-model="orderRideRequest.rideType.isDoNotApplySubsidy"
                  :disabled="isDisableDoNotApplyPartnerSubsidyField"
                  class="pb-0 font-weight-bold"
                >
                  Do not apply partner subsidy to this ride
                </el-checkbox>
                <div
                  class="help-block"
                  style="padding-top: 0"
                >
                  This option should only be selected if the customer wants to pay for the full cost of the ride and does not want it to
                  count towards their subsidized rides.
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-9 offset-sm-3">
              <div>
                <el-checkbox
                  id="isDoNotApplyExpiringCredits"
                  v-model="orderRideRequest.rideType.isDoNotApplyExpiringCredits"
                  class="pb-0 font-weight-bold"
                >
                  Do not apply expiring credits to this ride
                </el-checkbox>
              </div>
            </div>
          </div>

          <DeliveryInfoForm
            ref="deliveryInfoFormRef"
            variant="legacy"
            :serviceType="orderRideRequest.serviceType"
            :disabled="!isEditableForm"
            @update:result="onDeliveryInfoResult"
          />
          <hr>

          <div v-if="status.activeStatus">
            <div class="partner-estimate-header">
              <div class="partner-estimate-tabs">
                <button
                  v-for="(tab, index) in partnerEstimateTabs"
                  :key="tab.key"
                  type="button"
                  class="partner-estimate-tab"
                  :class="{ 'partner-estimate-tab--active': selectedPartnerEstimateIndex === index }"
                  @click="selectPartnerEstimateTab(index)"
                >
                  {{ tab.label }}
                </button>
              </div>

              <div
                v-if="selectedPartnerEstimate"
                class="partner-estimate-info"
              >
                <div class="partner-estimate-info-field">
                  <label class="partner-estimate-info-label">Partner Name</label>
                  <div class="partner-estimate-info-value">
                    {{ selectedPartnerEstimate.partnerName || selectedPartnerEstimate.name || '—' }}
                  </div>
                </div>
              </div>
            </div>

            <EstimationRideList
              :estimationList="estimationList"
              :estimationRide="estimationRide"
              :typeOfCar="orderRideRequest.typeOfCar"
              :isRideOutsideUS="isRideOutsideUS"
              :isInLyftAssistedTestGroup="isInLyftAssistedTestGroup"
              :isNashDelivery="isNashDelivery"
              @select-estimate="onSelectEstimate"
            />
          </div>

          <div
            v-if="status.activeStatus === 'RideStatus'"
            id="rideStatusSection"
            class="ride-state"
          >
            <message-html
              v-if="rideStatus.message"
              :has-top-line="false"
              :result-html="rideStatus.message"
              has-bottom-line
            />

            <OrderedRideStatus
              v-else
              :ride-status="rideStatus"
            />

            <div class="form-submit-button-group">
              <p-button
                round
                type="purple"
                @click="resetToGetEstimate"
              >
                OK
              </p-button>
            </div>
          </div>

          <template v-if="estimationRide && (status.activeStatus === 'RideEstimate' || status.activeStatus === 'UberSurge')">
            <EstimationRide
              v-model:operatorConfirmedSurgePriceWithCustomer="operatorConfirmedSurgePriceWithCustomer"
              :estimation-ride="estimationRide"
              :show-full-detail="!isHideRideEstimateFullDetail"
              :preferred-contact-warning="status.preferredContactWarning"
            />
          </template>

          <template v-if="status.activeStatus === 'RideEstimate' || status.activeStatus === 'UberSurge'">
            <div class="text-center">
              <p-button
                v-if="isOrderRideBtnVisible"
                :disabled="status.isLoading"
                native-type="submit"
                round
                type="purple"
              >
                {{ status.activeStatus === 'RideEstimate' ? 'Order Ride' : 'Accept Higher Price' }}
              </p-button>

              <p-button
                round
                type="warning"
                @click="cancelRideEstimation"
              >
                Cancel
              </p-button>
            </div>
          </template>

          <div
            v-if="status.activeStatus === 'WaitForDriver'"
            id="waitForDriverMatchSection"
            class="ride-state"
          >
            <div class="row justify-content-center">
              <p class="col-12 text-center">
                Thanks! We are requesting a ride for {{ estimationRide?.riderName }}.
              </p>
              <p class="col-12 text-center">
                This process can take up to two minute.
              </p>
              <p class="col-12 text-center notification-message mt-5 mobile-mt-5">
                <b>PLEASE DO NOT REFRESH THE PAGE.</b>
              </p>
            </div>
          </div>

          <message-html
            :has-top-line="false"
            :result-html="status.resultHtml"
            has-bottom-line
          />

          <TripExistsError
            v-if="status.isShowTripExistsError"
            :ride-info="rideExistsErrorInfo"
          />

          <template v-if="isPreferredContactNumberRequired && !isRiderPhoneNumberValid">
            <div class="pb-10 text-center text-red font-weight-bold">
              Preferred contact number is needed before getting an estimate.
            </div>
            <hr>
          </template>

          <div
            v-if="isEditableForm"
            class="get-estimation"
          >
            <div class="form-submit-button-group">
              <p-button
                :disabled="isEstimationBtnDisabled"
                native-type="submit"
                round
                type="purple"
              >
                {{ isAutoRetryRequest(orderRideRequest) ? 'Auto-Retry & Order' : 'Get Estimate' }}
              </p-button>

              <p-button
                round
                type="yellow"
                @click="resetForm()"
              >
                Reset
              </p-button>

              <p-button
                v-if="!selectedWillCallRideId && !isNashDelivery"
                round
                type="info"
                @click="transferForm()"
              >
                Schedule This Ride
              </p-button>
            </div>

            <BasicRideLimitValueUpgradeBanner class="mt-3" />
          </div>
        </customized-form>

        <!-- outside the form so it is not covered by the loading overlay while the driver is being matched -->
        <NextRideCheck
          class="mt-10"
          :caller="customerInfo"
          :total-rides="totalOrderInfo?.totalRides"
          :is-one-way="!isRoundTrip && isTransportation"
          :matching="isMatchingDriver"
        />

        <GourmetPopupDialog
          :is-show-dialog="status.isShowGourmetPopupDialog"
          @cancel="status.isShowGourmetPopupDialog = false"
          @submit="saveGourmetPopupResponse"
        />

        <GroceryPopupDialog
          :is-show-dialog="status.isShowGroceryPopupDialog"
          @cancel="status.isShowGroceryPopupDialog = false"
          @submit="saveGroceryPopupResponse"
        />

        <ShoppingPopupDialog
          :is-show-dialog="status.isShowShoppingPopupDialog"
          :shopping-places="shoppingPlaces"
          @cancel="status.isShowShoppingPopupDialog = false"
          @submit="saveShoppingPopupResponse"
        />

        <AddAccessibilityOptionsDialog
          :customerInfo="customerInfo"
          :isShowDialog="status.isShowAccessibilityPopupDialog"
          @cancel="status.isShowAccessibilityPopupDialog = false"
          @submit="saveAccessibilityOptionsPopupResponse"
        />
      </div>
    </div>
    <WillCallRidesForOrder
      v-if="showWillCallRides && isPhoneNumberValid"
      :is-disabled="!isEditableForm"
      :is-loading="status.isLoading"
      :request-id="rideStatus.requestId"
      :phone-number="orderRideRequest.phoneNumber"
      :selected-will-call-ride-id="selectedWillCallRideId"
      @copy-to-form="copyApprovedRideToForm"
    />
  </div>
</template>

<script lang="ts">
import { ref, toRefs, Ref, computed, watch, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import Moment from 'moment-timezone';
import _ from 'lodash';
import delay from 'yoctodelay';
import events from '@/events';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useReturnTripAlert } from '@/use/returnTripAlert';
import { useAddress } from '@/use/address';
import { usePartner } from '@/use/partner';
import { useRideTypes } from '@/use/rideTypes';
import { useRide } from '@/use/ride';
import { useGlobal } from '@/use/global';
import { sendGet, sendPost } from '@/Utils/api';
import { minutesToAutoRetryRequestTypes, emptyAddress, MULTI_STOP_WAIT_FEE_WARNING } from '@/constants/ride';
import { useRideStops } from '@/use/rideStops';
import { RideType, VendorName } from '@/models/rideType';
import { MedicalTripResult, RideServiceType, type RideEstimate, type DeliveryInfoFormResult } from '@/models/ride';
import { CallerAdditionalInfo, ShoppingPlace } from '@/models/caller';
import { CustomLocationType, AddressType } from '@/models/address';
import { checkAndRecordGourmetPopup } from '@/services/gourmet/checkAndRecordGourmetPopup';
import { checkAndRecordGroceryPopup } from '@/services/grocery/checkAndRecordGroceryPopup';
import { checkAndRecordShoppingPopup } from '@/services/caller/checkAndRecordShoppingPopup';
import { callerService } from '@/services/caller';
import { currentCallLog } from '@/services/callLogs/callLogsViewModel';
import { scheduledRideService } from '@/services/scheduledRide';
import { canOfferNashDelivery } from '@/services/operators/viewModel';
import { formatDeliveryValidationErrorMessage, isDeliveryServiceType } from '@/Utils/deliveryInfoForm';

import EstimationRide from '@/components/UIComponents/OrderingForms/EstimationRide.vue';
import OrderedRideStatus from '@/components/UIComponents/OrderingForms/OrderedRideStatus.vue';
import NextRideCheck from '@/components/Main/Operator/RideOrdering/NextRideCheck/NextRideCheck.vue';
import TripExistsError from '@/components/UIComponents/OrderingForms/TripExistsError.vue';
import EstimationRideList from '@/components/UIComponents/OrderingForms/EstimationRideList.vue';
import GroceryPopupDialog from '@/components/Main/Dialogs/GroceryPopup.vue';
import GourmetPopupDialog from '@/components/Main/Dialogs/GourmetPopup.vue';
import ShoppingPopupDialog from '@/components/Main/Dialogs/ShoppingPopup.vue';
import DriverAcceptedTextMessageNote from './DriverAcceptedTextMessageNote.vue';
import WillCallRidesForOrder from './WillCallRidesForOrder.vue';
import BasicRideLimitValueUpgradeBanner from './BasicRideLimitValueUpgradeBanner.vue';
import DeliveryInfoForm from './DeliveryInfoForm.vue';

import { uberPromoAbTestName, uberPromoPhoneNumber } from '@/Utils/constant';

import AddAccessibilityOptionsDialog from '../../Dialogs/AddAccessibilityOptions.vue';
import { ListType } from '@/models/app'
import type { Venue } from '@/models/venue'
import type { ScheduledRide } from '@/models/scheduledRide';
const pageId = 'order-rides';

const ADDITIONAL_INFO_FIELDS = [
  'isVisuallyImpaired',
  'isCane',
  'isElbowAssist',
  'isTraveling',
  'isWalker',
  'isWheelchair',
  'isPowerWheelchairScooter',
  'isHardOfHearing',
  'isServiceDog',
  'isOxygenTank',
  'isPoorMemory',
  'isNoBigCars',
  'isNoSmallCars',
  'isVerballyImpaired',
  'isAllowVoipPhoneNumbers',
  'isSlowWalker',
  'isAda',
  'isTurnOffAutoSavingAddress',
  'noAdditionalInfo',
];

export default {
  name: 'OrderRides',

  components: {
    EstimationRide,
    OrderedRideStatus,
    NextRideCheck,
    GroceryPopupDialog,
    GourmetPopupDialog,
    ShoppingPopupDialog,
    TripExistsError,
    DriverAcceptedTextMessageNote,
    AddAccessibilityOptionsDialog,
    WillCallRidesForOrder,
    EstimationRideList,
    BasicRideLimitValueUpgradeBanner,
    DeliveryInfoForm,
  },

  props: {
    formTitle: {
      type: String,
      default: 'Order rides on behalf of a registered user',
    },
  },

  emits: ['change-ride-form', 'choose-estimate', 'update:isNashDelivery'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const showEstimateStartCopyButton = ref<boolean>(false);
    const showEstimateEndCopyButton = ref<boolean>(false);
    const {
      customerInfo,
      customerStatus,
      customerPartner,
      customerPartnerCode,
      availablePartnerRidePurposes,
      rideAvailablePhoneNumber,
      isHideRideEstimateFullDetail,
      isDisableDeliveryServiceInterestedPopup,
      isSolanoCountyPartnership,
      setCustomerInfo,
      contactPhoneNumbers,
      isAllowedToExpandVehicleSearch,
      checkAndShowAdaDialog,
      customerFullName,
      totalOrderInfo,
    } = useCustomer();
    const { isBusinessAccount, hasUnresolvedDisputes, isPartnerCustomer } = toRefs(customerStatus);
    const {
      showSuccessPopupDialog,
      showErrorPopupDialog,
      showConfirmPopupDialog,
      showWarningPopupDialog,
      showConfirmPopupDialogWithValidation,
    } = useSwalDialog();
    const { showReturnTripAlertIfNeeded } = useReturnTripAlert();
    const {
      registrationStartAddress,
      registrationEndAddress,
      getStreetAddressByType,
      trimAddress,
      updateAddressWithGeocode,
      getAnchorBound,
      setRegistrationStartAddress,
      setRegistrationEndAddress,
      checkOutsideUSAddress,
    } = useAddress();
    const { partnerCode } = usePartner();
    const { getOrderRideTypes, getAvailableRideType } = useRideTypes();
    const {
      availableServiceTypeList,
      rideOrderData,
      setRideOrderData,
      checkSpecialRequestQuestions,
      checkPharmacyQuestions,
      doCancelScheduledRide,
      updateRepeatedScheduledRide,
      checkMedicalTripForDutchesPartnership,
      checkMedicalTripForDiamondBarPartnership,
      checkMedicalTripForRotcPartnership,
    } = useRide();
    const { teamLeadsAndSupsList } = useGlobal();
    const route = useRoute();

    const limitDurationMins: number = 60;

    const orderRideRequest: Ref<any> = ref({
      customRiderName: '',
      phoneNumber: '',
      riderPhone: '',
      partnerRidePurpose: '',
      typeOfCar: '',
      serviceType: RideServiceType.Transportation,
      rideType: {
        isEmergency: false,
        emergencyReason: '',
        approvedNameForEmergencyRide: '',
        badDrivers: false,
        overrideBigCar: false,
        overrideSmallCar: false,
        overrideBannedCar: false,
        isDoNotApplySubsidy: false,
        isDoNotApplyExpiringCredits: false,
      },
      defaultNoteMsg: {
        groceries: false,
        luggage: false,
      },
      noteToDriver: '',
      notesForLP: '',
      operatorNote: '',
      reason: '',
      venuesInstruction: '',
      minutesToAutoRetryRequest: '',
      minutesToAutoRetryRequestType: '',
      isMedicalTrip: false,
    });
    const isNeededToMonitorRide = ref(false);

    const startAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });
    const pickupVenues: Ref<Venue[]> = ref([]);
    const endAddress: Ref<CustomLocationType> = ref({ ...emptyAddress });
    const dropoffVenues: Ref<Venue[]> = ref([]);
    const {
      stops,
      canAddStop,
      hasStops,
      addStop,
      removeStop,
      hasDuplicateConsecutiveAddresses: checkConsecutiveAddresses,
    } = useRideStops();

    const markers: Ref<any> = ref([
      {
        position: {
          lat: 0.0,
          lng: 0.0,
        },
        initialPosition: {
          lat: 0.0,
          lng: 0.0,
        },
        index: 0,
        isDraggable: false,
        isShow: false,
        icon: '/static/img/pickup_marker.png',
        zIndex: 8000,
      },
      {
        position: {
          lat: 0.0,
          lng: 0.0,
        },
        initialPosition: {
          lat: 0.0,
          lng: 0.0,
        },
        index: 1,
        isDraggable: false,
        isShow: false,
        icon: '/static/img/dropoff_marker.png',
        zIndex: 8000,
      },
    ]);

    const estimationRide: Ref<RideEstimate | null> = ref(null);
    const estimationList: Ref<RideEstimate[]> = ref([]);
    const defaultEstimateData: Ref<any | null> = ref(null);
    const allPartnerEstimates: Ref<any[]> = ref([]);
    const selectedPartnerEstimateIndex = ref(0);
    const isEstimationOptionsMode = ref(false);
    const isInLyftAssistedTestGroup = ref(false);
    const operatorConfirmedSurgePriceWithCustomer = ref(false);

    const partnerEstimateTabs = computed(() => [
      { key: 'no-partner', label: 'No partner' },
      ...allPartnerEstimates.value.map((partnerEstimate, index) => ({
        key: `partner-${partnerEstimate.partnerId ?? index}`,
        label: `Partner #${index + 1}`,
      })),
    ]);

    const selectedPartnerEstimate = computed(() => {
      if (selectedPartnerEstimateIndex.value <= 0) {
        return null;
      }
      return allPartnerEstimates.value[selectedPartnerEstimateIndex.value - 1] || null;
    });

    const selectedPartnerId = computed(() => selectedPartnerEstimate.value?.partnerId);

    const rideStatus = ref({
      requestId: null,
      rideStatus: '',
      eta: '',
      driverName: '',
      driverPhone: '',
      driverPlate: '',
      carBrand: '',
      carModel: '',
      carColor: '',
      carImage: '',
      mapUrl: '',
      startLocation: '',
      endLocation: '',
      createdAt: '',
      txtCarColor: '',
      car: '',
      message: '',
    });
    const status = reactive({
      isLoading: false,
      isReset: false,
      activeStatus: '',
      loadingMessage: '',
      resultHtml: '',
      isShowTripExistsError: false,
      preferredContactWarning: '',
      isShowGroceryPopupDialog: false,
      isShowGourmetPopupDialog: false,
      isShowShoppingPopupDialog: false,
      isShowAccessibilityPopupDialog: false,
    });
    const shoppingPlaces: Ref<ShoppingPlace[]> = ref([]);
    const isRoundTrip = ref(false);
    const isExpandDriverSearch = ref(false);
    const isExpandVehicleSearch = ref(false);
    const isNeedToRefreshRightPanel = ref(false);

    const selectedWillCallRideId = ref<number | null>(null);
    const showWillCallRides = computed(() => !!customerPartner.value?.config?.allowWillCallRides);

    const startAddressComponent: Ref<any> = ref({});
    const endAddressComponent: Ref<any> = ref({});

    const driverNotePickUp = ref('');
    const driverNoteDropOff = ref('');
    const rideExistsErrorInfo: Ref<any> = ref(null);

    const newPreferredContactNumber = ref('');

    const deliveryInfoFormRef = ref<{
      reset: () => void;
      validate: () => { isValid: boolean; errors: string[] };
    } | null>(null);

    const deliveryInfoResult: Ref<DeliveryInfoFormResult> = ref({
      isValid: true,
      payload: null,
      notesForLP: '',
    });

    const onDeliveryInfoResult = (result: DeliveryInfoFormResult) => {
      deliveryInfoResult.value = result;
    };

    const isPhoneNumberValid = computed(() => util.isPhoneNumberValid(orderRideRequest.value.phoneNumber));

    const isRiderPhoneNumberValid = computed(() =>
      util.isPhoneNumberValid(orderRideRequest.value.riderPhone || newPreferredContactNumber.value),
    );

    const isPreferredContactNumberRequired = computed(() => !!orderRideRequest.value.phoneNumber);

    const isNewPreferredContactNumberVisible = computed(() => !orderRideRequest.value.riderPhone);

    const preferredVendorRideTypes = computed(() => customerPartner.value?.preferredVendorRideTypes || []);

    const availableRideTypes = computed(() => {
      const rideTypeList = getOrderRideTypes(customerPartnerCode.value || partnerCode.value, preferredVendorRideTypes.value);

      const isUberCall = currentCallLog.value?.toPhoneNumber === uberPromoPhoneNumber;
      const isUberPromoTest = customerInfo.value?.associatedAbTests?.some((abt: any) => abt?.abTestName === uberPromoAbTestName);
      if (isUberCall || isUberPromoTest) {
        return rideTypeList.filter((li) => li.label.startsWith('Uber'));
      } else {
        return rideTypeList;
      }
    });
    const isEstimationBtnDisabled = computed(() => {
      if (status.isLoading) {
        return true;
      }

      if (status.isReset) {
        return true;
      }

      if (!isPhoneNumberValid.value) {
        return false;
      }

      if (!isRiderPhoneNumberValid.value) {
        return true;
      }

      return false;
    });

    const isOrderRideBtnVisible = computed(() => {
      if (!estimationRide.value?.callRideFromEstimateLink) {
        return false;
      }

      if (!isPhoneNumberValid.value) {
        return false;
      }

      if (!isRiderPhoneNumberValid.value) {
        return false;
      }

      return true;
    });

    const isNeedToConfirmSideOrEntrance = computed(() => {
      const venue = pickupVenues.value?.[0];
      if (!venue || pickupVenueNote.value) {
        return false;
      }

      return Boolean(venue.zones?.length || venue.locations?.length);
    });

    const isEditableForm = computed(() => {
      return status.activeStatus === '';
    });

    const pickupDropoffNotes = computed(() => {
      return util.makePickupDropoffDriverNotes(driverNotePickUp.value, driverNoteDropOff.value);
    });

    const isTransportation = computed(() => orderRideRequest.value.serviceType === RideServiceType.Transportation);
    const isPharmacyOrder = computed(() => orderRideRequest.value.serviceType === RideServiceType.PharmacyDelivery);
    const isSpecialRequestOrder = computed(() => orderRideRequest.value.serviceType === RideServiceType.SpecialRequest);
    const isDelivery = computed(() => isDeliveryServiceType(orderRideRequest.value.serviceType));
    // Only operators in the Nash AB test group get the new delivery forms; everyone else keeps the question flow.
    const isNashDelivery = computed(() => isDelivery.value && canOfferNashDelivery.value);

    const isShowDoNotApplyPartnerSubsidyField = computed(() => {
      if (!customerInfo.value?.partner?.id) {
        // if a customer is not in a partnership, do not show this field
        return false;
      }

      return true;
    });

    const isDisableDoNotApplyPartnerSubsidyField = computed(() => {
      if (isTransportation.value) {
        return false;
      }
      if (isPharmacyOrder.value && customerInfo.value?.partner?.isPharmacy) {
        return false;
      }
      if (isSpecialRequestOrder.value && customerInfo.value?.partner?.isSpecialRequest) {
        return false;
      }
      return true;
    });

    const startAddressAnchorBound = computed(() => getAnchorBound(startAddress.value));
    const endAddressAnchorBound = computed(() => getAnchorBound(endAddress.value));
    const isPickupOutsideUS = computed(() => checkOutsideUSAddress(startAddress.value));
    const isDropoffOutsideUS = computed(() => checkOutsideUSAddress(endAddress.value));
    const isRideOutsideUS = computed(() => Boolean(isPickupOutsideUS.value || isDropoffOutsideUS.value));

    const copyApprovedRideToForm = (row: ScheduledRide) => {
      const id = row.id as number;
      selectedWillCallRideId.value = id;

      const startAddr = {
        streetAddress: (row.streetAddress1 as string) || '',
        streetAddress1: '',
        city: (row.city1 as string) || '',
        state: (row.state1 as string) || '',
        zip: (row.zip1 as string) || '',
        name: (row.locationName1 as string) || '',
        latitude: row.lat1 != null ? Number(row.lat1) : null,
        longitude: row.long1 != null ? Number(row.long1) : null,
        googlePlaceId: null,
        googlePlaceTypes: [],
      };
      const endAddr = {
        streetAddress: (row.streetAddress2 as string) || '',
        streetAddress1: '',
        city: (row.city2 as string) || '',
        state: (row.state2 as string) || '',
        zip: (row.zip2 as string) || '',
        name: (row.locationName2 as string) || '',
        latitude: row.lat2 != null ? Number(row.lat2) : null,
        longitude: row.long2 != null ? Number(row.long2) : null,
        googlePlaceId: null,
        googlePlaceTypes: [],
      };

      startAddress.value = { ...emptyAddress, ...startAddr };
      endAddress.value = { ...emptyAddress, ...endAddr };

      orderRideRequest.value.typeOfCar = getAvailableRideType({
        rideType: (row.rideType as string) || '',
        rideTypes: availableRideTypes.value,
        partnerCode: customerPartnerCode.value,
        customerInfo: customerInfo.value,
      });
      orderRideRequest.value.noteToDriver = (row.noteToDriver as string) || '';
      if (row.riderPhone) {
        orderRideRequest.value.riderPhone = row.riderPhone as string;
      }

      setMarkerPosition(Number(startAddr.latitude), Number(startAddr.longitude), 0, true);
      setMarkerPosition(Number(endAddr.latitude), Number(endAddr.longitude), 1, true);
    };

    const maxBirthDate = computed(() => Moment().subtract(18, 'years').format('YYYY-MM-DD'));

    const prefillOrderData = (data: any) => {
      orderRideRequest.value = {
        customRiderName: data.customRiderName || '',
        phoneNumber: data.phoneNumber || rideAvailablePhoneNumber.value || '',
        riderPhone: data.riderPhone || rideAvailablePhoneNumber.value || '',
        partnerRidePurpose: data.partnerRidePurpose || '',
        typeOfCar: getAvailableRideType({
          rideType: data.typeOfCar,
          rideTypes: availableRideTypes.value,
          partnerCode: customerPartnerCode.value,
          customerInfo: customerInfo.value,
        }),
        serviceType: data.serviceType || RideServiceType.Transportation,
        rideType: {
          isEmergency: false,
          emergencyReason: '',
          approvedNameForEmergencyRide: '',
          badDrivers: data.rideType?.badDrivers || false,
          overrideBigCar: data.rideType?.overrideBigCar || false,
          overrideSmallCar: data.rideType?.overrideSmallCar || false,
          overrideBannedCar: data.rideType?.overrideBannedCar || false,
          isDoNotApplySubsidy: data.rideType?.isDoNotApplySubsidy || false,
          isDoNotApplyExpiringCredits: data.rideType?.isDoNotApplyExpiringCredits || false,
        },
        defaultNoteMsg: {
          groceries: false,
          luggage: false,
        },
        noteToDriver: data.noteToDriver || '',
        notesForLP: data.notesForLP || '',
        operatorNote: '',
        reason: data.reason || '',
        venuesInstruction: '',
        minutesToAutoRetryRequest: data.minutesToAutoRetryRequest || '',
        minutesToAutoRetryRequestType: data.minutesToAutoRetryRequestType || '',
      };

      startAddress.value = _.cloneDeep(data.startAddress);
      endAddress.value = _.cloneDeep(data.endAddress);

      isNeededToMonitorRide.value = !!data.notesForLP;
      isExpandDriverSearch.value = data.isExpandDriverSearch;
      isExpandVehicleSearch.value = data.isExpandVehicleSearch;
      driverNotePickUp.value = data.driverNotePickUp;
      driverNoteDropOff.value = data.driverNoteDropOff;
    };

    const getOpenBackupAccount = async () => {
      const response = await sendGet('getOpenBackupAccount');
      orderRideRequest.value.phoneNumber = response.data.phoneNumber;
    };

    const swapAddress = () => {
      const tempVenues = _.cloneDeep(pickupVenues.value);
      pickupVenues.value = _.cloneDeep(dropoffVenues.value);
      dropoffVenues.value = _.cloneDeep(tempVenues);

      const temp = _.cloneDeep(startAddress.value);
      startAddress.value = _.cloneDeep(endAddress.value);
      endAddress.value = _.cloneDeep(temp);
      startAddressComponent.value.initAddressForm();
      endAddressComponent.value.initAddressForm();
    };

    const setVenuePlaces = (venues: Venue[], locationIndex: number) => {
      if (locationIndex === AddressType.PickUp) {
        pickupVenues.value = venues;
      } else {
        dropoffVenues.value = venues;
      }
    };

    const setVenueNote = (venueNote: string, venuesInstruction: string, locationIndex: number) => {
      if (locationIndex === AddressType.PickUp) {
        pickupVenueNote.value = venueNote;
        orderRideRequest.value.venuesInstruction = venuesInstruction;
      } else if (locationIndex === AddressType.DropOff) {
        driverNoteDropOff.value = venueNote ? '📝 Dropoff at ' + venueNote : '';
      }
    };

    const setMarkerPosition = (
      lat: number | string | null,
      lng: number | string | null,
      index: number,
      isMarkerChanged: boolean = true,
      isAddressChanged: boolean = false,
    ) => {
      util.updateMarkerPosition(markers.value[index], lat, lng, isAddressChanged);

      if (isMarkerChanged) {
        if (index === 0) {
          startAddress.value = updateAddressWithGeocode(startAddress.value, lat, lng);
        } else if (index === 1) {
          endAddress.value = updateAddressWithGeocode(endAddress.value, lat, lng);
        }
      }
    };

    const checkHavingSelectedOptions = () => {
      return ADDITIONAL_INFO_FIELDS.some((field) => customerInfo.value[field] === true);
    };

    const runCheckHavingNoAccessPopup = async () => {
      // only check accessibility options if there's a customer loaded
      const hasAccessibility = checkHavingSelectedOptions();
      if (!customerInfo.value?.id || hasAccessibility) {
        return getWebEstimate();
      }
      showAccessOptionsPopupDialog();
    };

    const showAccessOptionsPopupDialog = async () => {
      status.isShowAccessibilityPopupDialog = true;
    };

    const saveAccessibilityOptionsPopupResponse = async (data: CallerAdditionalInfo) => {
      status.isShowAccessibilityPopupDialog = false;

      status.resultHtml = '';
      status.loadingMessage = 'Add Accessibility Options Response...';
      status.isLoading = true;

      try {
        await callerService.updateCaller({
          callerId: customerInfo.value.id,
          data,
        });

        setCustomerInfo({
          ...customerInfo.value,
          ...data,
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isLoading = false;
      }
    };

    const checkRideSelectionAndShowWarnings = async () => {
      if (!estimationRide.value) {
        return;
      }

      await showReturnTripAlertIfNeeded(estimationRide.value.isNoReturnTrip, estimationRide.value.returnTripEtaMins);

      if (isPhoneNumberValid.value) {
        await checkNextDispatchedScheduledRideInHour();
      }

      if (isDelivery.value && !isNashDelivery.value) {
        const text = `
          Please let the customer know the estimate of the ride duration and ride cost is not accurate due to the wait time.<br />
          Example: the number of people at the pharmacy/special location can impact the time the driver needs to wait and thus increase the cost of the ride.
        `;
        await showWarningPopupDialog({
          text,
          title: 'Confirm with Client',
          type: 'html',
          icon: 'question',
        });
      }

      if (estimationRide.value.warningMessage) {
        showWarningPopupDialog({
          text: estimationRide.value.warningMessage,
        });
      }
    };

    const isAutoRetryRequest = ({ minutesToAutoRetryRequest }: { minutesToAutoRetryRequest?: number }) =>
      Boolean(minutesToAutoRetryRequest && Number(minutesToAutoRetryRequest) >= 5);

    const applySingleEstimationData = (estimationData: any) => {
      estimationData.isRoundTrip = isRoundTrip.value;
      estimationList.value = [];
      estimationRide.value = rideUtility.parseEstimationRide({
        estimationData,
      });
    };

    const applyMultiEstimationData = (estimationData: any) => {
      isInLyftAssistedTestGroup.value = Boolean(estimationData.isInLyftAssistedTestGroup);

      estimationList.value = rideUtility.parseEstimationRideMulti({
        apiResponse: estimationData,
        isRoundTrip: isRoundTrip.value,
      });

      const defaultOptionRide = estimationList.value.find((ride) => ride.isDefaultOption);
      const rideMatchingTheTypeOfCar = estimationList.value.find((ride) => ride.rideType === orderRideRequest.value.typeOfCar);

      const preselectedRide = defaultOptionRide ?? rideMatchingTheTypeOfCar;
      estimationRide.value = preselectedRide ?? null;
    };

    const applyEstimationData = async (estimationData: any, { showWarnings = true }: { showWarnings?: boolean } = {}) => {
      if (isEstimationOptionsMode.value) {
        applyMultiEstimationData(estimationData);
      } else {
        applySingleEstimationData(estimationData);
      }

      status.resultHtml = estimationData.errorMessage ? util.getErrorResponse({ message: estimationData.errorMessage }) : '';
      status.activeStatus = 'RideEstimate';

      if (showWarnings) {
        await checkRideSelectionAndShowWarnings();
      } else if (estimationRide.value?.warningMessage) {
        showWarningPopupDialog({
          text: estimationRide.value.warningMessage,
        });
      }
    };

    const setPartnerEstimatesFromResponse = (data: any) => {
      const partnerEstimates = data.allPartnerEstimates;
      defaultEstimateData.value = data;
      allPartnerEstimates.value = Array.isArray(partnerEstimates) ? partnerEstimates : [];

      if (allPartnerEstimates.value.length > 0) {
        selectedPartnerEstimateIndex.value = 1;
        return allPartnerEstimates.value[0];
      }

      selectedPartnerEstimateIndex.value = 0;
      return data;
    };

    const selectPartnerEstimateTab = async (index: number) => {
      if (index === selectedPartnerEstimateIndex.value) {
        return;
      }

      if (index === 0) {
        if (!defaultEstimateData.value) {
          return;
        }
        selectedPartnerEstimateIndex.value = 0;
        operatorConfirmedSurgePriceWithCustomer.value = false;
        await applyEstimationData(defaultEstimateData.value, { showWarnings: false });
        return;
      }

      const partnerEstimate = allPartnerEstimates.value[index - 1];
      if (!partnerEstimate) {
        return;
      }

      selectedPartnerEstimateIndex.value = index;
      operatorConfirmedSurgePriceWithCustomer.value = false;
      await applyEstimationData(partnerEstimate, { showWarnings: false });
    };

    const fetchEstimationRideSingle = async (postData: any) => {
      const { data } = await sendPost('getWebEstimate', postData);

      isEstimationOptionsMode.value = false;
      const estimationData = setPartnerEstimatesFromResponse(data);
      await applyEstimationData(estimationData);
    };

    const fetchEstimationRideMulti = async (postData: any) => {
      const {data} = await sendPost('getWebEstimateOptions', {...postData, rideType: undefined, isAllVendors: true});

      if (!rideUtility.hasEstimateOptions(data)) {
        status.resultHtml = util.getErrorResponse({ message: rideUtility.getNoEstimateOptionsMessage(data) });
        return;
      }

      isEstimationOptionsMode.value = true;
      const estimationData = setPartnerEstimatesFromResponse(data);
      await applyEstimationData(estimationData);
    }

    const getWebEstimate = async () => {
      const rideTypeItem = RideType.findByKey(orderRideRequest.value.typeOfCar);
      if (isRideOutsideUS.value && rideTypeItem?.vendor === VendorName.LYFT) {
        showWarningPopupDialog({
          text: 'Lyft rides are not supported outside US.',
        });
        return;
      }

      if (isNashDelivery.value) {
        const validation = deliveryInfoFormRef.value?.validate() ?? { isValid: false, errors: [] };
        if (!validation.isValid) {
          status.resultHtml = formatDeliveryValidationErrorMessage(validation.errors);
          return;
        }
      }

      const postData = getPostData();

      status.activeStatus = '';
      status.resultHtml = '';
      status.loadingMessage = 'Getting Estimation...';
      status.isLoading = true;

      resetRideEstimateData();

      try {
        if (isAutoRetryRequest(postData)) {
          await fetchEstimationRideSingle(postData);
        } else {
          await fetchEstimationRideMulti(postData);
        }
      } catch (error: any) {
        if (error.response && error.response.data) {
          const rideStatus = error.response.data.rideStatus;
          if (rideStatus) {
            fillAndDisplayRideDetails(error.response.data);
          } else if (error.response.data.code === 'current_trip_exists') {
            rideExistsErrorInfo.value = error.response.data.rideData;
            status.isShowTripExistsError = true;
          } else {
            status.resultHtml = util.getErrorResponse(error);
          }
        } else {
          status.resultHtml = util.getErrorResponse(error);
        }
      }
      status.isLoading = false;
    };

    const orderRide = async () => {
      if (!estimationRide.value) {
        return;
      }
      status.activeStatus = 'WaitForDriver';
      isMatchingDriver.value = true;
      let callRideFromEstimateLinkFinal = estimationRide.value.callRideFromEstimateLink;

      if (operatorConfirmedSurgePriceWithCustomer.value) {
        callRideFromEstimateLinkFinal += '&operatorConfirmedSurgePriceWithCustomer=true';
      }

      if (orderRideRequest.value.isMedicalTrip) {
        const isMedicalTripParam = 'isMedicalTrip';
        if (!callRideFromEstimateLinkFinal.includes(isMedicalTripParam)) {
          callRideFromEstimateLinkFinal += `&${isMedicalTripParam}=true`;
        }
      }

      if (selectedPartnerId.value != null) {
        callRideFromEstimateLinkFinal += `&partnerId=${selectedPartnerId.value}`;
      }

      try {
        status.loadingMessage = 'Ordering Ride...';
        status.isLoading = true;
        const response = await sendPost(callRideFromEstimateLinkFinal, {});

        selectedWillCallRideId.value = null;
        fillAndDisplayRideDetails(response.data);
      } catch (error: any) {
        if (error.response && error.response.data) {
          const responseStatus = error.response.data.status || error.response.status;
          // uber surge handle
          if (responseStatus === 409) {
            isMatchingDriver.value = false;
            fillAndDisplayUberSurgeError(error.response.data);
          } else if (responseStatus === 504) {
            status.activeStatus = '';
            status.resultHtml = util.getErrorResponse({
              message: `We are still waiting for a driver to accept. Please hit Get Customer Info again and check the Latest Ride info on right panel.`,
            });
          } else {
            const rideStatus = error.response.data.rideStatus;
            if (rideStatus) {
              fillAndDisplayRideDetails(error.response.data);
            } else {
              isMatchingDriver.value = false;
              status.activeStatus = '';
              status.resultHtml = util.getErrorResponse(error);
            }
          }
        } else {
          isMatchingDriver.value = false;
          status.activeStatus = '';
          status.resultHtml = util.getErrorResponse(error);
        }
      } finally {
        status.isLoading = false;
      }
    };

    const isMatchingDriver = ref(false);

    const resetRideEstimateData = () => {
      isMatchingDriver.value = false;
      estimationRide.value = null;
      estimationList.value = [];
      defaultEstimateData.value = null;
      allPartnerEstimates.value = [];
      selectedPartnerEstimateIndex.value = 0;
      isEstimationOptionsMode.value = false;
      rideExistsErrorInfo.value = null;
      selectedWillCallRideId.value = null;
      operatorConfirmedSurgePriceWithCustomer.value = false;
      status.isShowTripExistsError = false;
      isInLyftAssistedTestGroup.value = false;
    }

    const cancelRideEstimation = async () => {
      const isCancelRideEstimation = await showConfirmPopupDialog({
        title: 'Cancel this ride?',
        confirmButtonText: 'Cancel',
      });

      if (!isCancelRideEstimation) {
        return;
      }

      status.activeStatus = '';
      status.preferredContactWarning = '';

      if (isNeedToRefreshRightPanel.value) {
        isNeedToRefreshRightPanel.value = false;
        events.emit('refresh-right-panel');
      }

      orderRideRequest.value.operatorNote = '';

      resetRideEstimateData();
    };

    const onSelectEstimate = (rideEstimate: RideEstimate) => {
      const previousWarningMessage = estimationRide.value?.warningMessage;
      estimationRide.value = rideEstimate;
      status.activeStatus = 'RideEstimate';

      if (rideEstimate.warningMessage && rideEstimate.warningMessage !== previousWarningMessage) {
        showWarningPopupDialog({
          text: rideEstimate.warningMessage,
        });
      }
    };

    const pickupVenueNote = ref('');
    const riderClothes = ref('');
    const driverArrivals = computed<ListType[]>(() => {
      const name = orderRideRequest.value.customRiderName || customerInfo.value?.firstName || 'rider';
      return [
        { label: 'Call me', key: `Call ${name} upon arrival`, },
        { label: 'Honk the horn', key: `Honk horn upon arrival`, },
        { label: 'Exit car and meet me', key: `Exit car and meet ${name}`, },
      ];
    })
    const driverArrival = ref(driverArrivals.value[0].key);
    watch(driverArrivals, () => {
      driverArrival.value = driverArrivals.value[0].key;
    });

    watch([pickupVenueNote, riderClothes, driverArrival], () => {
      driverNotePickUp.value = [
        pickupVenueNote.value && `🚪 ${pickupVenueNote.value}`,
        riderClothes.value && `👚 Look for: ${riderClothes.value}`,
        driverArrival.value && `☎️ ${driverArrival.value}`,
      ].filter(Boolean).join('\n');
    });

    const resetToGetEstimate = async () => {
      isMatchingDriver.value = false;
      status.isReset = true;

      await delay(1000);

      status.isReset = false;
      status.activeStatus = '';
      status.preferredContactWarning = '';

      if (isNeedToRefreshRightPanel.value) {
        isNeedToRefreshRightPanel.value = false;
        events.emit('refresh-right-panel');
      }
    };

    const fillAndDisplayRideDetails = (json: any) => {
      const {
        eta,
        driverName,
        driverPhone,
        driverPlate,
        startLocation,
        destination,
        endLocation,
        createdAt,
        carImage,
        message,
        rideStatus: orderRideStatus,
        carBrand = '',
        carModel = '',
        carColor = '',
        mapUrl = null,
        requestId = null,
      } = json;

      rideStatus.value = {
        eta,
        driverName,
        driverPhone,
        driverPlate,
        carBrand,
        carModel,
        carColor,
        carImage,
        mapUrl,
        startLocation,
        createdAt,
        rideStatus: orderRideStatus,
        endLocation: destination || endLocation,
        txtCarColor: carColor,
        car: `${carColor} ${carBrand} ${carModel}`,
        message: message && util.setSuccessMessage(message),
        requestId,
      };

      status.activeStatus = 'RideStatus';
    };

    const fillAndDisplayUberSurgeError = (json: any) => {
      status.activeStatus = 'UberSurge';
      estimationRide.value = rideUtility.parseEstimationRide({
        estimationData: json,
      });
    };

    const getPostData = () => {
      trimAddress(startAddress.value);
      trimAddress(endAddress.value);

      const startStreetAddress = getStreetAddressByType(startAddress.value);
      const endStreetAddress = getStreetAddressByType(endAddress.value);

      let postData: any = rideUtility.makeEstimationPostData(
        {
          ...orderRideRequest.value,
          startAddress: startAddress.value,
          endAddress: endAddress.value,
          stops: stops.value,
          isBusinessAccount: isBusinessAccount.value,
          pickupDropoffNotes: pickupDropoffNotes.value,
        },
        startStreetAddress,
        endStreetAddress,
        true,
      );

      if (!postData.riderPhone) {
        postData.riderPhone = newPreferredContactNumber.value;
      }

      if (markers.value[1].isShow) {
        postData.endCoors = {
          lat: endAddress.value.latitude,
          lng: endAddress.value.longitude,
        };
      }

      postData.callerId = customerInfo.value?.id;
      postData.serviceType = orderRideRequest.value.serviceType;
      postData.willcallRideId = selectedWillCallRideId.value;

      // if ride is for special-request or pharmacy, we should not send noteToDriver to order the ride.
      if (isDelivery.value) {
        postData.noteToDriver = '';
      }

      if (isNashDelivery.value && deliveryInfoResult.value.payload) {
        postData = {
          ...postData,
          ...deliveryInfoResult.value.payload,
          notesForLP: deliveryInfoResult.value.notesForLP,
        };
      } else {
        postData.notesForLP = isNeededToMonitorRide.value ? orderRideRequest.value.notesForLP || '' : '';
      }

      return postData;
    };

    const checkNextDispatchedScheduledRideInHour = async () => {
      status.loadingMessage = 'Checking existing scheduled rides...';
      let scheduledRideIn1Hour: any = null;

      try {
        const response = await scheduledRideService.fetchScheduledRideRecords({
          phoneNumber: orderRideRequest.value.phoneNumber,
          isActive: true,
          isCanceled: false,
          isFinished: false,
        });

        for (const scheduledRide of response.rows) {
          const pickupDateTimeMoment = Moment.tz(scheduledRide.nextPickupTimeString, scheduledRide.timeZone);
          const currentDateTimeMoment = Moment.tz(scheduledRide.timeZone);

          if (currentDateTimeMoment.isAfter(pickupDateTimeMoment)) {
            continue;
          }

          var duration = Moment.duration(pickupDateTimeMoment.diff(currentDateTimeMoment));
          var differenceMinutes = duration.asMinutes() as number;

          if (differenceMinutes < limitDurationMins) {
            scheduledRideIn1Hour = rideUtility.formatScheduledRideData(scheduledRide);
            break;
          }
        }

        if (!scheduledRideIn1Hour) {
          return;
        }

        scheduledRideIn1Hour.rideMultiple
          ? await processRepeatedScheduledRide(scheduledRideIn1Hour)
          : await processSingleScheduledRide(scheduledRideIn1Hour);
      } catch (error) {
        console.log(error);
      }
    };

    const processSingleScheduledRide = async (scheduledRide: any) => {
      const startAddress = `${scheduledRide.streetAddress1}, ${scheduledRide.city1}`;
      const endAddress = `${scheduledRide.streetAddress2}, ${scheduledRide.city2}`;

      const isKeepScheduledRide = await showConfirmPopupDialog({
        text: `There is a scheduled ride (${scheduledRide.id}) within the next hour at <b>${scheduledRide.nextPickupTimeString}</b> from <b>${startAddress}</b> to <b>${endAddress}</b>.<br>Would you like to still keep the scheduled ride or cancel it?`,
        title: 'Keep scheduled ride?',
        confirmButtonText: 'Keep',
        cancelButtonText: 'Cancel',
        type: 'html',
      });

      if (isKeepScheduledRide) {
        return;
      }

      return doCancelScheduledRide(scheduledRide.id, 'Order the ride manually');
    };

    const processRepeatedScheduledRide = async (scheduledRide: any) => {
      const startAddress = `${scheduledRide.streetAddress1}, ${scheduledRide.city1}`;
      const endAddress = `${scheduledRide.streetAddress2}, ${scheduledRide.city2}`;

      const response = await showConfirmPopupDialog({
        text: `There is a ride within the next hour for repeated scheduled ride (${scheduledRide.id}) at <b>${scheduledRide.nextPickupTimeString}</b> from <b>${startAddress}</b> to <b>${endAddress}</b>.<br>Would you like to keep or skip the next dispatch of this repeatedscheduled ride or cancel the entire repeated scheduled ride?`,
        title: 'How would you like to proceed?',
        confirmButtonText: 'Keep Next Scheduled Ride',
        cancelButtonText: 'Cancel Entire Repeated Scheduled Ride',
        type: 'html',
        customClass: 'repeated-scheduled-process-popup',
        denyButtonText: 'Skip Next Scheduled Ride',
      });

      if (response.value) {
        return;
      }

      if (response.value === false) {
        const skipDate = util.formatDateTime(scheduledRide.nextPickupTimeString, 'YYYY-MM-DD');
        const skipScheduledRideDates = scheduledRide.skipScheduledRideDates || [];
        const skipDateIndex = skipScheduledRideDates.findIndex((x: string) => x === skipDate);
        if (skipDateIndex > -1) {
          return;
        }

        skipScheduledRideDates.push(skipDate);

        scheduledRide.skipScheduledRideDates = skipScheduledRideDates;
        return updateRepeatedScheduledRide(scheduledRide);
      }

      return doCancelScheduledRide(scheduledRide.id, 'Ride is orderd manually');
    };

    const checkMedicalTripForPartnerships = async () => {
      orderRideRequest.value.isMedicalTrip = false;
      status.loadingMessage = 'Checking medical trip for partnership';
      status.isLoading = true;

      const resultDutchesPartnership = await checkMedicalTripForDutchesPartnership(startAddress.value, endAddress.value);
      const resultDiamondBarPartnership = await checkMedicalTripForDiamondBarPartnership(startAddress.value, endAddress.value);
      const resultRotcPartnership = await checkMedicalTripForRotcPartnership(startAddress.value, endAddress.value);

      status.isLoading = false;

      if (
        resultDutchesPartnership === MedicalTripResult.VALIDATION_FAILED ||
        resultDiamondBarPartnership === MedicalTripResult.VALIDATION_FAILED ||
        resultRotcPartnership === MedicalTripResult.VALIDATION_FAILED
      ) {
        return false;
      }

      if (
        resultDutchesPartnership === MedicalTripResult.USER_DECLINED ||
        resultDiamondBarPartnership === MedicalTripResult.USER_DECLINED ||
        resultRotcPartnership === MedicalTripResult.USER_DECLINED
      ) {
        orderRideRequest.value.rideType.isDoNotApplySubsidy = true;
      }

      if (
        resultDutchesPartnership === MedicalTripResult.NO_MEDICAL_PLACE ||
        resultDiamondBarPartnership === MedicalTripResult.NO_MEDICAL_PLACE
      ) {
        orderRideRequest.value.rideType.isDoNotApplySubsidy = true;
      }

      if (
        resultDutchesPartnership === MedicalTripResult.VALIDATION_PASSED ||
        resultDiamondBarPartnership === MedicalTripResult.VALIDATION_PASSED ||
        resultRotcPartnership === MedicalTripResult.VALIDATION_PASSED
      ) {
        orderRideRequest.value.isMedicalTrip = true;
      }

      return true;
    };

    const submitForm = async () => {
      if (isNeededToMonitorRide.value && !orderRideRequest.value.notesForLP?.trim()) {
        showErrorPopupDialog({
          text: 'Please enter a reason for monitoring this ride.',
        });
        return;
      }

      if (checkConsecutiveAddresses(startAddress.value, endAddress.value)) {
        showErrorPopupDialog({
          text: 'Two consecutive addresses cannot be the same — please remove or change the duplicate stop.',
        });
        return;
      }

      if (status.activeStatus === 'RideEstimate' || status.activeStatus === 'UberSurge') {
        if (hasUnresolvedDisputes.value) {
          showWarningPopupDialog({
            text: 'Services on this account cannot be ordered because there are credit card disputes!',
          });

          return;
        }

        orderRide();
      } else {
        status.isShowTripExistsError = false;

        if (isNeedToConfirmSideOrEntrance.value) {
          showWarningPopupDialog({
            text: 'Please select or enter "Side or Entrance" for Pickup location',
          });

          return;
        }

        const resultMedicalTrip = await checkMedicalTripForPartnerships();

        if (!resultMedicalTrip) {
          return;
        }

        const isBirthdayConfirmed = await checkAndUpdateCustomerBirthday();

        if (!isBirthdayConfirmed) {
          return;
        }

        const response = await checkServiceTypeQuestions();

        if (!response) {
          return;
        }

        status.preferredContactWarning = '';
        if (isRiderPhoneNumberValid.value) {
          checkPreferredNumber();
        }

        await runGourmetGroceryPopupTestAndGetEstimate();

        // if the caller have no accessibility options, the operator select the options in the popup
        await runCheckHavingNoAccessPopup();

        await checkAndShowAdaDialog();
      }
    };

    // Control-group deliveries still confirm DOB here; Nash test collects it via DeliveryInfoForm.
    const checkAndUpdateCustomerBirthday = async () => {
      if (isTransportation.value || isNashDelivery.value) {
        return true;
      }

      // no need to check birthday if there is no customer info b/c it is just for getting estimation
      if (!customerInfo.value.id) {
        return true;
      }

      if (customerInfo.value.dob) {
        return true;
      }

      const birthday = await showConfirmPopupDialogWithValidation({
        title: 'Please confirm the birthday with customer',
        input: 'date',
        inputPlaceholder: 'Please input birthday',
        validationText: 'Please input birthday',
        confirmButtonText: 'Confirm',
        dateValidation: {
          maxDate: maxBirthDate.value,
        },
      });

      if (!birthday) {
        return false;
      }

      status.isLoading = true;
      try {
        await callerService.updateCaller({
          callerId: customerInfo.value.id,
          data: {
            dob: birthday,
          },
        });

        setCustomerInfo({
          ...customerInfo.value,
          dob: birthday,
        });

        isNeedToRefreshRightPanel.value = true;

        return true;
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isLoading = false;
      }

      return false;
    };

    // Deliveries in the Nash test group collect this information through DeliveryInfoForm instead.
    const checkServiceTypeQuestions = async () => {
      if (isTransportation.value || isNashDelivery.value) {
        return true;
      }

      // no need to check questions if there is no customer phone number
      if (!orderRideRequest.value.phoneNumber) {
        return true;
      }

      // no need to check questions if we asked the questions
      if (orderRideRequest.value.operatorNote) {
        return true;
      }

      const response = isSpecialRequestOrder.value ? await checkSpecialRequestQuestions() : await checkPharmacyQuestions();

      if (!response) {
        return false;
      }

      orderRideRequest.value.operatorNote = response.notes;
      if (response.rideType) {
        orderRideRequest.value.typeOfCar = response.rideType;
      }

      if (response.isRoundTrip !== undefined) {
        isRoundTrip.value = response.isRoundTrip;
      }

      return true;
    };

    const checkPreferredNumber = async () => {
      if (!isNewPreferredContactNumberVisible.value) {
        // if user choose the phone number from the dropdown, we don't need to check preferred contact number
        return;
      }

      const existingPhoneNumber = contactPhoneNumbers.value.find((item: any) => item.key === newPreferredContactNumber.value);
      if (existingPhoneNumber) {
        return;
      }

      try {
        const response = await callerService.getCallerLovedOneIdName(newPreferredContactNumber.value);

        status.preferredContactWarning = util.checkAndGetBelongsWarningMessage(customerInfo.value, response);

        if (response) {
          showWarningPopupDialog({
            text: 'Please make sure this is the phone number the user wants for this ride!',
            title: status.preferredContactWarning,
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

    const runGourmetGroceryPopupTestAndGetEstimate = async () => {
      // we only want to run the gourmet/grocery/shopping ab test for operators and if there is a phone number that doesn't end with 0
      if (!orderRideRequest.value.phoneNumber || orderRideRequest.value.phoneNumber.endsWith('0')) {
        return;
      }

      if (isDisableDeliveryServiceInterestedPopup.value) {
        return;
      }

      if (currentCallLog.value?.purpose?.includes('registrations') || currentCallLog.value?.callType?.includes('registrations')) {
        return;
      }

      status.isLoading = true;
      status.resultHtml = '';
      status.loadingMessage = 'Checking Gourmet/Grocery/Shopping Popup Test...';

      const postData = getPostData();

      const locations = [
        {
          lat: Number(postData.startCoors.lat),
          lng: Number(postData.startCoors.lng),
        },
      ];

      if (postData.endCoors) {
        locations.push({
          lat: Number(postData.endCoors.lat),
          lng: Number(postData.endCoors.lng),
        });
      }

      try {
        const isGourmetPopup = await checkAndRecordGourmetPopup({
          phoneNumber: orderRideRequest.value.phoneNumber,
          locations,
        });

        if (isGourmetPopup) {
          showGourmetPopupDialog();
          return;
        }

        const isGroceryPopup = await checkAndRecordGroceryPopup({
          phoneNumber: orderRideRequest.value.phoneNumber,
          locations,
        });

        if (isGroceryPopup) {
          showGroceryPopupDialog();
          return;
        }

        shoppingPlaces.value = await checkAndRecordShoppingPopup({
          phoneNumber: orderRideRequest.value.phoneNumber,
          locations,
        });

        const isShoppingPopup = shoppingPlaces.value.length > 0;

        if (isShoppingPopup && !isPartnerCustomer.value) {
          showShoppingPopupDialog();
        }
      } catch (error) {
        status.resultHtml = util.getGraphqlError(error);
      } finally {
        status.isLoading = false;
      }
    };

    const showGroceryPopupDialog = () => {
      status.isShowGroceryPopupDialog = true;
    };

    const saveGroceryPopupResponse = async ({
      clientResponse,
      notInterestedReason,
    }: {
      clientResponse: string;
      notInterestedReason: string;
    }) => {
      status.isShowGroceryPopupDialog = false;

      status.resultHtml = '';
      status.loadingMessage = 'Saving Grocery Popup Response...';
      status.isLoading = true;

      try {
        await callerService.saveGroceryPopupResponse({
          phoneNumber: orderRideRequest.value.phoneNumber,
          clientResponse,
          notInterestedReason,
        });

        showSuccessPopupDialog({
          text: 'The grocery popup response has been saved successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      status.isLoading = false;
    };

    const showGourmetPopupDialog = () => {
      status.isShowGourmetPopupDialog = true;
    };

    const saveGourmetPopupResponse = async ({
      clientResponse,
      notInterestedReason,
    }: {
      clientResponse: string;
      notInterestedReason: string;
    }) => {
      status.isShowGourmetPopupDialog = false;

      status.resultHtml = '';
      status.loadingMessage = 'Saving Gourmet Popup Response...';
      status.isLoading = true;

      try {
        await callerService.saveGourmetPopupResponse({
          phoneNumber: orderRideRequest.value.phoneNumber,
          clientResponse,
          notInterestedReason,
        });

        showSuccessPopupDialog({
          text: 'The gourmet popup response has been saved successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      status.isLoading = false;
    };

    const showShoppingPopupDialog = () => {
      status.isShowShoppingPopupDialog = true;
    };

    const saveShoppingPopupResponse = async ({
      clientResponse,
      notInterestedReason,
    }: {
      clientResponse: string;
      notInterestedReason: string;
    }) => {
      status.isShowShoppingPopupDialog = false;

      status.resultHtml = '';
      status.loadingMessage = 'Saving Shopping Popup Response...';
      status.isLoading = true;

      try {
        await callerService.saveShoppingPopupResponse({
          phoneNumber: orderRideRequest.value.phoneNumber,
          clientResponse,
          notInterestedReason,
        });

        showSuccessPopupDialog({
          text: 'The shopping popup response has been saved successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
      status.isLoading = false;
    };

    const resetForm = () => {
      const fromRegistration = route.query.fromRegistration === 'true';

      orderRideRequest.value = {
        phoneNumber: rideAvailablePhoneNumber.value || '',
        customRiderName: '',
        riderPhone: rideAvailablePhoneNumber.value || '',
        typeOfCar: getAvailableRideType({
          rideTypes: availableRideTypes.value,
          partnerCode: customerPartnerCode.value,
          customerInfo: customerInfo.value,
        }),
        serviceType: RideServiceType.Transportation,
        rideType: {
          isEmergency: false,
          emergencyReason: '',
          approvedNameForEmergencyRide: '',
          badDrivers: false,
          overrideBigCar: false,
          overrideSmallCar: false,
          overrideBannedCar: false,
          isDoNotApplySubsidy: false,
          isDoNotApplyExpiringCredits: false,
        },
        defaultNoteMsg: {
          groceries: false,
          luggage: false,
        },
        noteToDriver: '',
        notesForLP: '',
        operatorNote: '',
        reason: '',
        venuesInstruction: '',
        minutesToAutoRetryRequest: '',
        minutesToAutoRetryRequestType: '',
        isMedicalTrip: false,
      };

      deliveryInfoFormRef.value?.reset();
      isNeededToMonitorRide.value = false;

      status.resultHtml = '';
      status.isShowTripExistsError = false;
      rideExistsErrorInfo.value = null;
      status.preferredContactWarning = '';

      startAddressComponent.value?.initAddressForm(true);
      endAddressComponent.value?.initAddressForm(true);

      startAddress.value = _.cloneDeep(
        fromRegistration && !!registrationStartAddress.value ? registrationStartAddress.value : emptyAddress,
      );
      endAddress.value = _.cloneDeep(fromRegistration && !!registrationEndAddress.value ? registrationEndAddress.value : emptyAddress);
      stops.value = [];
      markers.value = markers.value.filter((marker: any, index: number) => index < 2);

      isExpandDriverSearch.value = customerInfo.value?.isExpandDriverSearch || false;
      isExpandVehicleSearch.value = customerInfo.value?.isExpandVehicleSearch || false;
      isRoundTrip.value = false;
      driverNotePickUp.value = '';
      driverNoteDropOff.value = '';
      riderClothes.value = '';
      driverArrival.value = driverArrivals.value[0].key;

      newPreferredContactNumber.value = '';

      resetRideEstimateData();
    };

    const transferForm = () => {
      isMatchingDriver.value = false;
      setRideOrderData({
        ...orderRideRequest.value,
        isNeededToMonitorRide: isNeededToMonitorRide.value,
        isExpandDriverSearch: isExpandDriverSearch.value,
        isExpandVehicleSearch: isExpandVehicleSearch.value,
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        driverNotePickUp: driverNotePickUp.value,
        driverNoteDropOff: driverNoteDropOff.value,
      });

      emit('change-ride-form');
    };

    const checkAndResetBuggedRide = () => {
      if (isTransportation.value) {
        return;
      }

      // rideAvailablePhoneNumber comes from RightPanel
      // if rideAvailablePhoneNumber and orderRideRequest.value.phoneNumber are same, it is not bugged ride
      // (b/c bugged account phone numbers start with 999 and customer phone numbers never have 999)
      // otherwise we need to reset phone number to disallow bugged ride for spacial request or pharmacy ride
      if (rideAvailablePhoneNumber.value && rideAvailablePhoneNumber.value === orderRideRequest.value.phoneNumber) {
        return;
      }

      orderRideRequest.value.phoneNumber = '';
    };

    const copyToStartAddress = () => {
      startAddress.value = { ...startAddress, ...registrationStartAddress.value };
    };

    const copyToEndAddress = () => {
      endAddress.value = registrationEndAddress.value;
    };

    const checkAndShowPopupForSolanoCountyPartnership = async () => {
      if (!isSolanoCountyPartnership.value) {
        return;
      }

      const questionHtml = `
        When speaking with callers, please ask the following question verbatim:<br />
        Would you like to process this request through Uber/Lyft or a local taxi vendor?"<br /><br />
        <strong>Important:</strong>
        If "Local Taxi Vendor" is selected, the system will display the scheduled ride form instead of the standard ride ordering form.
      `;

      const isUberLyftRequest = await showConfirmPopupDialog({
        text: questionHtml,
        title: 'Operator Notice',
        confirmButtonText: 'Uber/Lyft',
        cancelButtonText: 'Local Taxi Vendor',
        type: 'html',
      });

      if (isUberLyftRequest) {
        return;
      }

      orderRideRequest.value.typeOfCar = RideType.Stawav.key;

      emit('change-ride-form');
    };

    watch(customerInfo, (value: any, oldValue: any) => {
      if (value && oldValue && value.id && value.id === oldValue.id) {
        return;
      }

      resetForm();

      setRegistrationStartAddress(null);
      setRegistrationEndAddress(null);

      if (value.id) {
        checkAndShowPopupForSolanoCountyPartnership();
      }
    });

    watch(
      availableRideTypes,
      () => {
        orderRideRequest.value.typeOfCar = getAvailableRideType({
          rideTypes: availableRideTypes.value,
          partnerCode: customerPartnerCode.value,
          customerInfo: customerInfo.value,
        });
      },
      {
        immediate: true,
      },
    );

    watch(startAddress, (value: any, oldValue: any) => {
      if (!value) {
        return;
      }

      const isAddressChanged = util.isAddressChanged(value, oldValue);
      const { latitude, longitude } = value;

      setMarkerPosition(Number(latitude), Number(longitude), 0, false, isAddressChanged);
    });

    watch(endAddress, (value: any, oldValue: any) => {
      if (!value) {
        return;
      }

      const isAddressChanged = util.isAddressChanged(value, oldValue);
      const { latitude, longitude } = value;

      setMarkerPosition(Number(latitude), Number(longitude), 1, false, isAddressChanged);
    });

    watch(
      () => orderRideRequest.value.defaultNoteMsg.groceries,
      (value: boolean) => {
        if (value) {
          orderRideRequest.value.noteToDriver += orderRideRequest.value.noteToDriver ? ' ' : '';
          orderRideRequest.value.noteToDriver += 'The rider has groceries.';
        } else {
          const pattern = /The rider has groceries./gi;
          orderRideRequest.value.noteToDriver = orderRideRequest.value.noteToDriver?.replace(pattern, '');
        }
      },
    );

    watch(
      () => orderRideRequest.value.defaultNoteMsg.luggage,
      (value: boolean) => {
        if (value) {
          orderRideRequest.value.noteToDriver += orderRideRequest.value.noteToDriver ? ' ' : '';
          orderRideRequest.value.noteToDriver += 'The rider has luggage.';
        } else {
          const pattern = /The rider has luggage./gi;
          orderRideRequest.value.noteToDriver = orderRideRequest.value.noteToDriver?.replace(pattern, '');
        }
      },
    );

    watch(
      () => orderRideRequest.value.serviceType,
      () => {
        if (isPharmacyOrder.value) {
          orderRideRequest.value.reason = 'Pharmacy Delivery';
        } else {
          orderRideRequest.value.reason = '';
        }

        if (isDelivery.value) {
          orderRideRequest.value.typeOfCar = 'uberX';
          orderRideRequest.value.rideType.isDoNotApplySubsidy = true;

          checkAndResetBuggedRide();
        }
      },
    );

    watch(
      isNashDelivery,
      (value) => {
        emit('update:isNashDelivery', value);
      },
      { immediate: true },
    );

    watch(isExpandDriverSearch, (value: boolean) => {
      orderRideRequest.value.rideType.badDrivers = value;
    });

    watch(isExpandVehicleSearch, (value: boolean) => {
      orderRideRequest.value.rideType.overrideBigCar = value;
      orderRideRequest.value.rideType.overrideSmallCar = value;
      orderRideRequest.value.rideType.overrideBannedCar = value;
    });

    watch(
      registrationStartAddress,
      (val: CustomLocationType | null) => {
        showEstimateStartCopyButton.value = !!val?.streetAddress;
      },
      { immediate: true },
    );

    watch(
      registrationEndAddress,
      (val: CustomLocationType | null) => {
        showEstimateEndCopyButton.value = !!val?.streetAddress;
      },
      { immediate: true },
    );

    watch(
      rideOrderData,
      async (val: any) => {
        if (!val) {
          return;
        }

        await nextTick();
        prefillOrderData(val);
      },
      {
        immediate: true,
      },
    );

    watch(
      estimationRide,
      (value: RideEstimate | null) => {
        if (value) {
          checkRideSelectionAndShowWarnings();
        }
      }
    );

    onMounted(() => {
      resetForm();
      checkAndShowPopupForSolanoCountyPartnership();
    });

    onBeforeUnmount(() => {
      setRideOrderData({
        ...rideOrderData.value,
        ...orderRideRequest.value,
        isNeededToMonitorRide: isNeededToMonitorRide.value,
        isExpandDriverSearch: isExpandDriverSearch.value,
        isExpandVehicleSearch: isExpandVehicleSearch.value,
        startAddress: startAddress.value,
        endAddress: endAddress.value,
        driverNotePickUp: driverNotePickUp.value,
        driverNoteDropOff: driverNoteDropOff.value,
      });
    });

    return {
      pageId,
      orderRideRequest,
      startAddress,
      pickupVenues,
      endAddress,
      dropoffVenues,
      markers,
      estimationRide,
      estimationList,
      allPartnerEstimates,
      partnerEstimateTabs,
      selectedPartnerEstimateIndex,
      selectedPartnerEstimate,
      selectPartnerEstimateTab,
      isInLyftAssistedTestGroup,
      operatorConfirmedSurgePriceWithCustomer,
      rideStatus,
      status,
      isRoundTrip,
      isExpandDriverSearch,
      isExpandVehicleSearch,
      isNeededToMonitorRide,
      customerInfo,
      customerFullName,
      teamLeadsAndSupsList,
      isPhoneNumberValid,
      isRiderPhoneNumberValid,
      isPreferredContactNumberRequired,
      startAddressComponent,
      endAddressComponent,
      availablePartnerRidePurposes,
      availableRideTypes,
      preferredVendorRideTypes,
      availableServiceTypeList,
      isEstimationBtnDisabled,
      isOrderRideBtnVisible,
      isEditableForm,
      isBusinessAccount,
      shoppingPlaces,
      util,
      isTransportation,
      rideExistsErrorInfo,
      isShowDoNotApplyPartnerSubsidyField,
      isDisableDoNotApplyPartnerSubsidyField,
      startAddressAnchorBound,
      endAddressAnchorBound,
      contactPhoneNumbers,
      isNewPreferredContactNumberVisible,
      newPreferredContactNumber,
      showEstimateStartCopyButton,
      showEstimateEndCopyButton,
      minutesToAutoRetryRequestTypes,
      isAllowedToExpandVehicleSearch,
      currentCallLog,
      customerPartner,
      isRideOutsideUS,
      isSpecialRequestOrder,
      isPharmacyOrder,
      deliveryInfoFormRef,
      onDeliveryInfoResult,

      isAutoRetryRequest,
      getOpenBackupAccount,
      swapAddress,
      setMarkerPosition,
      cancelRideEstimation,
      resetToGetEstimate,
      saveGroceryPopupResponse,
      saveGourmetPopupResponse,
      saveShoppingPopupResponse,
      saveAccessibilityOptionsPopupResponse,
      resetForm,
      transferForm,
      submitForm,
      copyToStartAddress,
      copyToEndAddress,

      showAccessOptionsPopupDialog,
      totalOrderInfo,
      isMatchingDriver,

      driverNoteDropOff,
      driverNotePickUp,

      pickupVenueNote,
      setVenueNote,
      driverArrival,
      driverArrivals,
      riderClothes,
      setVenuePlaces,

      onSelectEstimate,
      showWillCallRides,
      selectedWillCallRideId,
      copyApprovedRideToForm,
      isHideRideEstimateFullDetail,
      isDelivery,
      isNashDelivery,
      canOfferNashDelivery,
      stops,
      canAddStop,
      hasStops,
      addStop,
      removeStop,
      MULTI_STOP_WAIT_FEE_WARNING,
    };
  },
};
</script>

<style lang="scss">
.order-rides {
  .form-check {
    padding-bottom: 5px;
  }

  .ride-state {
    font-size: 14px;
  }

  .bug-button {
    border: none;
    outline: none;
    font-size: 27px !important;
    min-width: 30px;
    padding-left: 10px !important;
    padding-top: 0 !important;
    background: transparent;
  }

  .form-submit-button-group {
    @media (max-width: 576px) {
      button {
        margin-bottom: 5px !important;
      }
    }
  }

  .pickup-dropoff-notes {
    padding: 5px 10px;
    background: #f4f3ef;
    font-size: 14px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    color: #000;
  }

  .partner-estimate-header {
    max-width: 500px;
    margin: 0 auto 12px;
  }

  .partner-estimate-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }

  .partner-estimate-tab {
    padding: 8px 14px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    cursor: pointer;

    &:hover {
      color: #6d509c;
    }

    &--active {
      color: #6d509c;
      font-weight: 700;
      border-bottom-color: #6d509c;
    }
  }

  .partner-estimate-info {
    font-size: 14px;
    border-radius: 5px;
    background: #fff1b3;
    padding: 12px 4px;
  }

  .partner-estimate-info-field {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .partner-estimate-info-label {
    display: block;
    margin: 0 0 4px;
    padding: 0 12px;
    font-size: 14px;
    font-weight: 700;
  }

  .partner-estimate-info-value {
    padding: 0 12px;
    text-align: left;
    font-size: 14px;
  }

  .stop-title-row {
    display: flex;
    align-items: center;

    > span {
      margin-right: 16px;
    }
  }

  .add-stop-row .btn {
    margin-left: 16px;
  }

  #noteToDriver {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}
</style>
